import { ChangeEvent, useCallback, useEffect } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { CompanyI } from '~src/interfaces/company.interface';
import { AnswerValue, CardI, SelectionValue } from '~src/interfaces/question.interface';
import { Scale as ScaleI, ScaleVisualization } from '~src/interfaces/scale.interface';
import { StoreI } from '~src/rootReducer';
import { translateContent } from '~src/services/translationServices';
import SvgIcon from '~src/shared/SvgIcon';
import Scale from '~src/shared/features/scale/Scale';
import CustomTextArea from '~src/shared/form/inputs/CustomTextArea';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import { FieldType } from '~src/types';

import { Topic } from '~src/interfaces/topic.interface';
import { fetchTopicById } from '~src/modules/topics/actions/topicActions';
import Card from './Card';
import { ResetButton, ResetText } from './questionCard.sc';
import {ErrorWithParams, validatorMaxLength5000} from "~src/services/validators";

interface OwnProps {
  currentCard: CardI;
  onSubmit: (values: AnswerValue, currentCard: CardI) => void | undefined;
  reset: (currentCard: CardI) => Promise<unknown>;
  saveLoading: boolean;
  nextCard: () => void;
  prevCard: (() => void) | null;
  showSpinner: boolean;
  onChangeComment: (comment: string) => void;
}

interface StateProps {
  initialValues: {
    comment?: string | null;
  };
  topicId: string;
  topic?: Topic;
  company: CompanyI | null;
  scales: { [key: string]: ScaleI };
  showTopicOnCard: boolean;
  isCommentEnabled: boolean;
}

interface DispatchProps {
  fetchTopicById: (id: string) => void;
}

type Props = OwnProps & StateProps & DispatchProps;

const QuestionCard = (props: Props) => {
  const { t } = useTranslation();
  useEffect(() => {
    if (!props.topic) {
      props.fetchTopicById(props.topicId);
    }
  }, [props.topicId]);

  const showCommentOption = useCallback(
    () => (
      <Field
        name="comment"
        placeholder={t('x.questions.card.comment.placeholder')}
        component={renderCommentFieldError}
        validate={validatorMaxLength5000}
        labelKey="x.questions.card.comment.placeholder"
      />
    ),
    [],
  );

  const renderCommentFieldError = ({ input, placeholder, meta: { error, touched }, labelKey }: FieldType<string>) => {
    const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      input.onChange(event);
      props.onChangeComment(event.target.value);
    };
    return (
      <CustomTextArea
        {...input}
        error={touched ? (error as ErrorWithParams) : undefined}
        label={placeholder || 'UNKNOWN'}
        minHeight={60}
        autoExpand
        labelKey={labelKey}
        onChange={onChange}
      />
    );
  };

  const renderScale = (change: (field: string, value: any) => void, saveLoading: boolean) => {
    const card = props.currentCard;
    const currentScale = getScale();
    const labels =
      currentScale.visualization === ScaleVisualization.McqOptions
        ? currentScale.multipleChoiceOptions
        : currentScale.labels;
    const rating = (typeof card.answer.answer === 'number') ? card.answer.answer : (card.answer.multipleChoiceAnswers || null);

    return (
      <Scale
        t={t}
        type={currentScale.visualization}
        labels={labels}
        rating={rating}
        onRatingSelect={change}
        saveLoading={saveLoading}
        isMultipleChoiceEnabled={currentScale.isMultipleChoiceEnabled}
      />
    );
  };

  const getScale = (): ScaleI => {
    const card = props.currentCard;
    const scaleIdx = (card.answer && card.answer.scaleVersionId) || card.question.scaleVersionId;
    return props.scales[scaleIdx];
  };

  const isForcedVerticalScale = (): boolean => {
    const scale = getScale();

    switch (scale.visualization) {
      case ScaleVisualization.Labels:
        return scale.visualization === ScaleVisualization.Labels && scale.labels.length > 4;
        break;
      case ScaleVisualization.McqOptions:
        return (
          scale.visualization === ScaleVisualization.McqOptions && scale.multipleChoiceOptions.length > 4
        );
        break;
      default:
        return false;
    }
  };

  const submitting = (values: AnswerValue | SelectionValue) => {
    props.onSubmit(values as AnswerValue, props.currentCard);
  };

  const resetThisCard = () => {
    props.onChangeComment('');
    void props.reset(props.currentCard);
  };

  const renderCornerButton = () => (
    <HoverInfoIcon
      infoText={{
        key: 'x.questions.card.reset.description',
        txt: t('x.questions.card.reset.description'),
      }}
      trigger={
        <ResetButton onClick={resetThisCard}>
          <SvgIcon name="iconReset" />
          <ResetText data-translation="x.questions.card.reset.text">
            {t('x.questions.card.reset.text')}
          </ResetText>
        </ResetButton>
      }
      notMobile
    />
  );

  const card = props.currentCard;
  if (!card) {
    return <LoadingSpinner color="white" />;
  }

  const questionText = card
    ? translateContent(card.question[card.selfAssessment ? 'selfTitle' : 'title']) ||
      translateContent(card.question['title'])
    : '';

  return (
    <Card
      subject={card.about}
      cornerButton={card.answer.answeredAt ? renderCornerButton : undefined}
      key={`questionForm_${card.id}`}
      onSubmit={submitting}
      actionPanel={renderScale}
      commentField={showCommentOption()}
      saveLoading={props.saveLoading}
      company={props.company}
      title={questionText}
      topic={props.showTopicOnCard && props.topic ? translateContent(props.topic.name) : ''}
      description={translateContent(card.question.description)}
      showSpinner={props.showSpinner}
      initialValues={props.initialValues}
      verticalScale={isForcedVerticalScale()}
      t={t}
      isCommentEnabled={props.isCommentEnabled}
    />
  );
};

const mapStateToProps = (state: StoreI, props: OwnProps): StateProps => {
  const topicId = state.user.questions.byId[props.currentCard.id]?.question.topicId;
  const topic = state.topics.byId[topicId];
  const company = state.user.session.company;
  const showTopicOnCard = company?.features?.survey?.showTopicOnCard || false;
  const isCommentEnabled = state.user.questions.byId[props.currentCard.id]?.question.isCommentEnabled;

  return {
    initialValues: {
      comment: props.currentCard && props.currentCard.answer ? props.currentCard.answer.comment : null,
    },
    topicId,
    topic,
    company,
    scales: state.scales.byId,
    showTopicOnCard,
    isCommentEnabled,
  };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchTopicById: (id) => dispatch(fetchTopicById(id)),
});

const QuestionCardC = connect(mapStateToProps, mapDispatchToProps)(QuestionCard);

export default QuestionCardC;
